import { getTemplate, type CropAreaPercent, type SlideContent } from 'lorena-templates'
import { TextField } from './fields/TextField'
import { TextAreaField } from './fields/TextAreaField'
import { ImageCropField } from './fields/ImageCropField'
import { RangeField } from './fields/RangeField'

interface ImageProps {
  imageUrl?: string
  originalImageUrl?: string
  imageTransform?: CropAreaPercent | null
  onUploadImage: (file: File, originalFile?: File | null) => void | Promise<void>
  onTransformChange: (area: CropAreaPercent) => void
  uploadingImage?: boolean
}

interface Props extends ImageProps {
  templateId: string
  content: SlideContent
  onChange: (content: SlideContent) => void
}

/**
 * Dispatcher genérico: lê `fieldsSchema` do template e renderiza o campo
 * certo pra cada um — nunca um formulário hardcoded por template.
 * Ver plano de implementação, seção "Fluxo da timeline".
 */
export function SlideForm({
  templateId,
  content,
  onChange,
  imageUrl,
  originalImageUrl,
  imageTransform,
  onUploadImage,
  onTransformChange,
  uploadingImage,
}: Props) {
  const template = getTemplate(templateId)
  if (!template) return <p style={{ color: '#b3261e' }}>Template "{templateId}" não encontrado.</p>

  const setField = (key: string, value: unknown) => onChange({ ...content, [key]: value })

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {template.fieldsSchema.map((field) => {
        if (field.type === 'image') {
          return (
            <ImageCropField
              key={field.key}
              field={field}
              imageUrl={imageUrl}
              originalImageUrl={originalImageUrl}
              transform={imageTransform}
              onUploadFile={onUploadImage}
              onTransformChange={onTransformChange}
              uploading={uploadingImage}
            />
          )
        }
        switch (field.type) {
          case 'text': {
            const value = String(content[field.key] ?? field.defaultValue ?? '')
            return <TextField key={field.key} field={field} value={value} onChange={(v) => setField(field.key, v)} />
          }
          case 'textarea': {
            const value = String(content[field.key] ?? field.defaultValue ?? '')
            return <TextAreaField key={field.key} field={field} value={value} onChange={(v) => setField(field.key, v)} />
          }
          case 'range': {
            const rawValue = Number(content[field.key] ?? field.defaultValue ?? field.min ?? 0)
            const value = Number.isFinite(rawValue) ? rawValue : Number(field.defaultValue ?? field.min ?? 0)
            return <RangeField key={field.key} field={field} value={value} onChange={(v) => setField(field.key, v)} />
          }
          default:
            return (
              <p key={field.key} style={{ color: 'var(--muted)', fontSize: 13 }}>
                Campo "{field.label}" ({field.type}) ainda não implementado nesta fase.
              </p>
            )
        }
      })}
    </div>
  )
}
